import { useEffect, useMemo, useState } from 'react';

import { isNil } from 'ramda';
import { useSelector } from 'react-redux';

import { useSnackbar } from 'hooks/useSnackbar';
import useValidationSchema from 'hooks/useValidationSchema';
import { accountIdSelector } from 'store/user/selectors';
import { confirmAlert } from 'utils/alert';
import api from 'utils/axios';

import validationSchema from './validationSchema';

export function useCosts() {
  const validate = useValidationSchema(validationSchema);

  return {
    validate,
  };
}

const buildEndpoint = (accId, id) => `/acc/${accId}/itinerary_costs` + (isNil(id) ? '' : `/${id}`);

export function useCostsTemplates() {
  const accId = useSelector(accountIdSelector);
  const onSnackbar = useSnackbar();
  const [templates, setTemplates] = useState([]);
  const templateOptions = useMemo(
    () => templates.map((template) => ({ value: template.id, label: template.name, policies: template.policies })),
    [templates],
  );
  const updatableTemplateOptions = useMemo(
    () => templateOptions.filter((option) => option.policies?.update),
    [templateOptions],
  );
  const findTemplateById = (id) => templates.find((item) => item.id === id);
  const fetchTemplates = async () => {
    const response = await api.get(buildEndpoint(accId));
    setTemplates(response.data.data);
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const setTemplate = (templateId, form) => {
    const template = findTemplateById(templateId);

    confirmAlert({
      confirmButtonText: 'Confirm',
      onConfirm: async () => {
        try {
          form.reset(template.data);
          onSnackbar(`Template '${template.name}' has been applied.`);
        } catch (error) {
          onSnackbar(`Template '${template.name}' couldn't be applied.`, true);
          throw error;
        }
      },
      title: 'Warning!',
      hintBottom: (
        <>
          Form data will be reset to &quot;{template.name}&quot; template.
          <br />
          <strong>Are you sure you want to do this?</strong>
          <br />
        </>
      ),
    });
  };

  const deleteTemplate = (templateId) => {
    const template = findTemplateById(templateId);

    confirmAlert({
      confirmButtonText: 'Delete',
      onConfirm: async () => {
        try {
          await api.delete(buildEndpoint(accId, templateId));
          await fetchTemplates();
          onSnackbar(`Template '${template.name}' has been deleted.`);
        } catch (error) {
          onSnackbar(`Template '${template.name}' couldn't be deleted. Please try again.`, true);
          throw error;
        }
      },
      title: `Are you sure you want to delete '${template.name}' template?`,
    });
  };

  const createTemplate = async (values) => {
    try {
      await api.post(buildEndpoint(accId), values);
      await fetchTemplates();
      onSnackbar(`Template '${values.name}' has been created.`);
    } catch (error) {
      onSnackbar(`Template '${values.name}' couldn't be created.`, true);
    }
  };

  const updateTemplate = async (templateId, values) => {
    confirmAlert({
      confirmButtonText: 'Confirm',
      onConfirm: async () => {
        try {
          await api.patch(buildEndpoint(accId, templateId), values);
          await fetchTemplates();
          onSnackbar(`Template '${values.name}' has been updated.`);
        } catch (error) {
          onSnackbar(`Template '${values.name}' couldn't be updated.`, true);
          throw error;
        }
      },
      title: `Are you sure you want to update '${values.name}' template?`,
    });
  };

  return {
    templates,
    templateOptions,
    updatableTemplateOptions,
    setTemplate,
    deleteTemplate,
    createTemplate,
    updateTemplate,
  };
}
